/**
 * Enhanced Jewelry Code Service
 * Frontend service for jewelry ID generation using Categories table integration
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

class JewelryCodeService {
  
  /**
   * Get next jewelry code for Add Inventory (by category ID)
   * @param {number} categoryId - Category ID from categories table
   * @param {string} format - Code format ('dash', 'underscore', 'none')
   * @returns {Promise<string>} Next jewelry code
   */
  static async getNextCodeByCategoryId(categoryId, format = 'dash') {
    try {
      const response = await fetch(`${API_BASE_URL}/jewelry-code/next-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          categoryId,
          format
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to generate jewelry code');
      }

      return data.nextCode;
    } catch (error) {
      console.error('Error getting next jewelry code:', error);
      throw error;
    }
  }

  /**
   * Get next jewelry code by category code (for direct code access)
   * @param {string} categoryCode - Category code (e.g., 'DNS', 'DER', 'GCH')
   * @param {string} format - Code format
   * @returns {Promise<string>} Next jewelry code
   */
  static async getNextCodeByCategoryCode(categoryCode, format = 'dash') {
    try {
      const response = await fetch(`${API_BASE_URL}/jewelry-code/next-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          categoryCode,
          format
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to generate jewelry code');
      }

      return data.nextCode;
    } catch (error) {
      console.error('Error getting next jewelry code:', error);
      throw error;
    }
  }

  /**
   * Get all active child categories for dropdowns
   * @returns {Promise<Array>} Array of active child categories
   */
  static async getActiveCategories() {
    try {
      const response = await fetch(`${API_BASE_URL}/jewelry-code/categories`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch categories');
      }

      return data.categories;
    } catch (error) {
      console.error('Error fetching active categories:', error);
      throw error;
    }
  }

  /**
   * Generate code for upload functionality (flexible identifier)
   * @param {string|number} categoryIdentifier - Category ID, code, or name
   * @param {string} format - Code format
   * @returns {Promise<string>} Generated jewelry code
   */
  static async generateCodeForUpload(categoryIdentifier, format = 'dash') {
    try {
      const response = await fetch(`${API_BASE_URL}/jewelry-code/generate-for-upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          categoryIdentifier,
          format
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to generate code for upload');
      }

      return data.generatedCode;
    } catch (error) {
      console.error('Error generating code for upload:', error);
      throw error;
    }
  }

  /**
   * Validate category for code generation
   * @param {number} categoryId - Category ID to validate
   * @returns {Promise<Object>} Validation result
   */
  static async validateCategory(categoryId) {
    try {
      const response = await fetch(`${API_BASE_URL}/jewelry-code/validate-category`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          categoryId
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to validate category');
      }

      return data.validation;
    } catch (error) {
      console.error('Error validating category:', error);
      throw error;
    }
  }

  /**
   * Get code generation statistics
   * @returns {Promise<Object>} Statistics about code usage
   */
  static async getStatistics() {
    try {
      const response = await fetch(`${API_BASE_URL}/jewelry-code/statistics`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch statistics');
      }

      return data.statistics;
    } catch (error) {
      console.error('Error fetching statistics:', error);
      throw error;
    }
  }

  /**
   * Get next sequence number for a category
   * @param {string} categoryCode - Category code
   * @returns {Promise<Object>} Next sequence information
   */
  static async getNextSequence(categoryCode) {
    try {
      const response = await fetch(`${API_BASE_URL}/jewelry-code/next-sequence/${categoryCode}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to get next sequence');
      }

      return data;
    } catch (error) {
      console.error('Error getting next sequence:', error);
      throw error;
    }
  }

  /**
   * Bulk generate codes for batch operations
   * @param {number} categoryId - Category ID
   * @param {number} count - Number of codes to generate
   * @param {string} format - Code format
   * @returns {Promise<Array>} Array of generated codes
   */
  static async bulkGenerate(categoryId, count, format = 'dash') {
    try {
      const response = await fetch(`${API_BASE_URL}/jewelry-code/bulk-generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          categoryId,
          count,
          format
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to bulk generate codes');
      }

      return data.generatedCodes;
    } catch (error) {
      console.error('Error bulk generating codes:', error);
      throw error;
    }
  }

  /**
   * Create new child category with auto-generated code
   * @param {Object} categoryData - Category data
   * @returns {Promise<Object>} Created category with code
   */
  static async createChildCategory(categoryData) {
    try {
      const response = await fetch(`${API_BASE_URL}/jewelry-code/create-child-category`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(categoryData)
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to create child category');
      }

      return data.category;
    } catch (error) {
      console.error('Error creating child category:', error);
      throw error;
    }
  }

  /**
   * Check if a specific code is available
   * @param {string} code - Code to check
   * @param {number} excludeId - ID to exclude from check (for updates)
   * @returns {Promise<boolean>} Whether code is available
   */
  static async checkCodeAvailability(code, excludeId = null) {
    try {
      const url = `${API_BASE_URL}/jewelry-code/check-availability/${encodeURIComponent(code)}`;
      const queryParams = excludeId ? `?excludeId=${excludeId}` : '';
      
      const response = await fetch(url + queryParams, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to check code availability');
      }

      return data.isAvailable;
    } catch (error) {
      console.error('Error checking code availability:', error);
      throw error;
    }
  }

  /**
   * Format code with different separators
   * @param {string} code - Base code
   * @param {string} format - Format type ('dash', 'underscore', 'none')
   * @returns {string} Formatted code
   */
  static formatCode(code, format = 'dash') {
    if (!code) return '';
    
    // Remove existing separators
    const cleanCode = code.replace(/[-_]/g, '');
    
    switch (format) {
      case 'dash':
        return cleanCode.replace(/([A-Z]+)(\d+)/, '$1-$2');
      case 'underscore':
        return cleanCode.replace(/([A-Z]+)(\d+)/, '$1_$2');
      case 'none':
        return cleanCode;
      default:
        return code;
    }
  }

  /**
   * Parse code to extract category and sequence
   * @param {string} code - Jewelry code to parse
   * @returns {Object} Parsed code components
   */
  static parseCode(code) {
    if (!code) return { category: '', sequence: 0, isValid: false };
    
    const match = code.match(/^([A-Z]+)[-_]?(\d+)$/);
    
    if (match) {
      return {
        category: match[1],
        sequence: parseInt(match[2]),
        isValid: true,
        original: code
      };
    }
    
    return {
      category: '',
      sequence: 0,
      isValid: false,
      original: code
    };
  }

  /**
   * Get category suggestions based on name
   * @param {string} categoryName - Category name to match
   * @param {Array} categories - Available categories
   * @returns {Array} Matching categories
   */
  static getCategorySuggestions(categoryName, categories) {
    if (!categoryName || !categories) return [];
    
    const searchTerm = categoryName.toLowerCase();
    
    return categories.filter(cat => 
      cat.name.toLowerCase().includes(searchTerm) ||
      cat.code.toLowerCase().includes(searchTerm) ||
      cat.parent_name.toLowerCase().includes(searchTerm)
    );
  }
}

export default JewelryCodeService;